import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { absoluteApiUrl, apiFetch } from './api.js';
import { type FilterParamEntries, appendFilterEntries, filterEntriesKey } from './filter-params.js';

export type ApiProjectMapStage =
  | 'pre_filing'
  | 'filed'
  | 'under_review'
  | 'approved'
  | 'permitted'
  | 'under_construction'
  | 'completed'
  | 'unknown';

export type ApiProjectMapBounds = [[number, number], [number, number]] | null;

export interface ApiProjectMapStageCount {
  stage: ApiProjectMapStage;
  count: number;
}

export interface ApiProjectMapSummary {
  total: number;
  new_count: number;
  mapped: number;
  unmapped: number;
  bounds: ApiProjectMapBounds;
  mapped_stage_counts: ApiProjectMapStageCount[];
  as_of: string;
  map_version: string;
}

export interface ApiProjectClusterMember {
  id: string;
  name: string;
  stage: ApiProjectMapStage;
}

export interface ApiProjectClusterMembersResponse {
  projects: ApiProjectClusterMember[];
  next_cursor: string | null;
}

export function projectMapSummaryQueryKey(
  workspaceId: string | undefined,
  filter: FilterParamEntries | undefined,
  staticViewId: string | undefined,
) {
  return [
    'project-map-summary',
    workspaceId,
    filterEntriesKey(filter),
    staticViewId ?? null,
  ] as const;
}

function projectMapScopeQuery(input: {
  workspaceId: string;
  filter?: FilterParamEntries;
  staticViewId?: string;
  mapVersion?: string;
}): string {
  const params = new URLSearchParams({ workspace_id: input.workspaceId });
  if (input.staticViewId) params.set('view_id', input.staticViewId);
  if (input.mapVersion) params.set('map_version', input.mapVersion);
  appendFilterEntries(params, input.filter);
  return params.toString();
}

export function useProjectMapSummary(
  workspaceId: string | undefined,
  filter: FilterParamEntries | undefined,
  staticViewId: string | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<ApiProjectMapSummary> {
  return useQuery({
    queryKey: projectMapSummaryQueryKey(workspaceId, filter, staticViewId),
    enabled: Boolean(workspaceId) && (options?.enabled ?? true),
    staleTime: 30_000,
    queryFn: async () => {
      const query = projectMapScopeQuery({
        workspaceId: workspaceId ?? '',
        filter,
        staticViewId,
      });
      return apiFetch<ApiProjectMapSummary>(`/project-map/summary?${query}`);
    },
  });
}

export function projectMapTileTemplate(input: {
  workspaceId: string;
  filter?: FilterParamEntries;
  staticViewId?: string;
  mapVersion: string;
}): string {
  const query = projectMapScopeQuery(input);
  return `${absoluteApiUrl('/project-map/tiles/{z}/{x}/{y}')}?${query}`;
}

export function projectMapClusterMembersPath(input: {
  clusterId: string;
  workspaceId: string;
  filter?: FilterParamEntries;
  staticViewId?: string;
  mapVersion: string;
  cursor?: string | null;
}): string {
  const query = new URLSearchParams(
    projectMapScopeQuery({
      workspaceId: input.workspaceId,
      filter: input.filter,
      staticViewId: input.staticViewId,
      mapVersion: input.mapVersion,
    }),
  );
  if (input.cursor) query.set('cursor', input.cursor);
  return `/project-map/clusters/${encodeURIComponent(input.clusterId)}?${query.toString()}`;
}

export function isProjectMapTileUrl(url: string): boolean {
  try {
    const apiTileBase = new URL(absoluteApiUrl('/project-map/tiles/'));
    const candidate = new URL(url, apiTileBase.origin);
    return (
      candidate.origin === apiTileBase.origin && candidate.pathname.startsWith(apiTileBase.pathname)
    );
  } catch {
    return false;
  }
}

export function authenticatedMapRequest(
  url: string,
  accessToken: string | undefined,
): { url: string; headers?: Record<string, string> } {
  if (!isProjectMapTileUrl(url) || !accessToken) return { url };
  return { url, headers: { Authorization: `Bearer ${accessToken}` } };
}
